import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, PartyPopper } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("checking"); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const res = await axios.get(`${API}/api/payments/checkout-status/${sessionId}`, {
        withCredentials: true
      });

      setPaymentData(res.data);

      if (res.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (res.data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (err) {
      console.error("Error checking payment status:", err);
      if (pollCount < maxAttempts - 1) {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  const getPlanName = (planId) => {
    const names = {
      'getraenke': 'Getränke-Plan',
      'cafe': 'Café-Plan',
      'restaurant': 'Restaurant All-in-One'
    };
    return names[planId] || planId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="subscription-success-page">
      <div className="max-w-md w-full">
        {status === "checking" && (
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Zahlung wird überprüft...</h2>
            <p className="text-slate-400">Bitte warte einen Moment</p>
            <div className="mt-4 h-1 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-500"
                style={{ width: `${(pollCount / 10) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <div className="relative">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              </div>
              <PartyPopper className="absolute top-0 right-1/4 w-6 h-6 text-amber-400 animate-bounce" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Willkommen bei MargeVio!</h2>
            <p className="text-slate-400 mb-6">
              Dein <span className="text-emerald-400 font-semibold">{getPlanName(paymentData?.plan)}</span> ist jetzt aktiv.
            </p>

            {paymentData && (
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Plan:</span>
                  <span className="text-white font-medium">{getPlanName(paymentData.plan)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Betrag:</span>
                  <span className="text-white font-medium">{paymentData.amount?.toFixed(2).replace('.', ',')}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Status:</span>
                  <span className="text-emerald-400 font-medium">Bezahlt</span>
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold"
              data-testid="goto-dashboard"
            >
              Zum Dashboard
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Zahlung fehlgeschlagen</h2>
            <p className="text-slate-400 mb-6">
              Die Zahlung konnte nicht verarbeitet werden. Bitte versuche es erneut.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/pricing")}
                className="w-full py-5 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white"
              >
                Erneut versuchen
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="w-full py-5 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Zurück zum Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
